import {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer
} from 'react';
import ReactGA from 'react-ga4';
import { brandConfig } from '../../config';
import { GA_MEASUREMENT_ID } from '../../config-global';
import { ReservationGeneral } from '../../models/owner-portal/reservationGeneral';
import { ActionMapType } from '../types';
import { useAuthContext } from '../useAuthContext';
import { GAEventBase } from './GAEvent';

const VERBOSE_LOGGING = false;

// Initialize Analytics
if (!GA_MEASUREMENT_ID) {
  throw new Error('Missing required GA_MEASUREMENT_ID');
}

enum Types {
  INITIAL = 'INITIAL',
  LOADING = 'LOADING'
}

type AnalyticsStateType = {
  isInitialized: boolean;
};

export enum itemCategory {
  PAYMENT = 'payment'
}

export enum paymentEvent {
  ADD_PAYMENT_METHOD = 'add_payment_method',
  UPDATE_PAYMENT_METHOD = 'update_payment_method',
  DELETE_PAYMENT_METHOD = 'delete_payment_method',
  SET_DEFAULT_PAYMENT_METHOD = 'set_default_payment_method'
}

type Payload = {
  [Types.INITIAL]: {
    isInitialized: boolean;
  };
  [Types.LOADING]: {
    isLoading: boolean;
  };
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const reducer = (state: AnalyticsStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      isInitialized: true,
      isLoading: false
    };
  }
  if (action.type === Types.LOADING) {
    return {
      ...state,
      isLoading: true
    };
  }
  return state;
};

const initialReducerState: AnalyticsStateType = {
  isInitialized: false
};

export interface AnalyticsContextI extends AnalyticsStateType {
  sendAnalytic: (fieldObject: any) => void;
  setAnalytic: (fieldObject: any) => void;
  sendEvent: (event: GAEventBase) => void;
  sendPaymentEvent: (category: itemCategory, event: paymentEvent) => void;
}

const initialState = {
  ...initialReducerState,
  sendAnalytic: () => {
    throw new Error('Analytics not initialized.');
  },
  setAnalytic: () => {
    throw new Error('Analytics set not initialized.');
  },
  sendEvent: () => {
    throw new Error('Analytics event not initialized.');
  },
  sendPaymentEvent: () => {
    throw new Error('Analytics event not initialized.');
  }
};

export const AnalyticsContext = createContext<AnalyticsContextI>(initialState);

export function AnalyticsProvider({ children }: PropsWithChildren) {
  ReactGA.initialize([
    {
      trackingId: GA_MEASUREMENT_ID!,
      gaOptions: {},
      gtagOptions: {}
    }
    // {
    //   Another ID...
    // }
  ]);
  const { user } = useAuthContext();
  const [state, dispatch] = useReducer(reducer, initialReducerState);

  const setLoading = (isLoading: boolean) => {
    dispatch({
      type: Types.LOADING,
      payload: {
        isLoading
      }
    });
  };

  const { isInitialized, send: gaSend, set, event: gaEvent, reset } = ReactGA;

  // check initialized analytics
  useEffect(() => {
    dispatch({
      type: Types.INITIAL,
      payload: {
        isInitialized
      }
    });
  }, [isInitialized]);

  useEffect(() => {
    if (isInitialized && user && user?.id) {
      set({ userId: user.id });
    } else {
      reset();
    }
  }, [isInitialized, user, reset, set]);

  const sendPaymentEvent = useCallback(
    (category: itemCategory, event: paymentEvent) => {
      if (!isInitialized) {
        console.warn('Analytics is not initailized.');
        setLoading(false);
        return;
      }

      setLoading(true);
      const params = {
        category,
        event
      };
      gaEvent(category, params);
      setLoading(false);
    },
    [gaEvent, isInitialized]
  );

  const event = useCallback(
    (concreteEvent: GAEventBase) => {
      if (!isInitialized) {
        console.warn('Analytics is not initailized.');
        return;
      }

      VERBOSE_LOGGING && console.warn('Sending analytics Event', concreteEvent.name);
      setLoading(true);
      gaEvent(concreteEvent.name, concreteEvent.getParams());
      setLoading(false);
    },
    [gaEvent, isInitialized]
  );

  const send = useCallback(
    (fileObject: any) => {
      if (!isInitialized) {
        console.warn('Analytics is not initailized.');
        return;
      }
      gaSend(fileObject);
    },
    [gaSend, isInitialized]
  );

  const value = useMemo(
    () => ({
      ...state,
      sendAnalytic: send,
      setAnalytic: set,
      sendEvent: event,
      sendPaymentEvent
    }),
    [state, send, set, event, sendPaymentEvent]
  );

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

interface AnalyticsCustomItem {
  item_id: string;
  item_name: string;
  affiliation: string;
  discount: number;
  index: number;
  item_brand: string;
  item_category: string;
  price: number;
  quantity: number;
}

export function createAnalyticsPaymentItem(
  item: ReservationGeneral | AnalyticsCustomItem,
  index: number,
  category?: string,
  amountOverride?: number
): AnalyticsCustomItem {
  if (item instanceof ReservationGeneral) {
    if (!category) throw new Error('Category is required.');
    return {
      item_id: item.id,
      item_name: category,
      affiliation: item.name,
      discount: 0,
      index,
      item_brand: brandConfig.name,
      item_category: category,
      price: amountOverride || 0,
      quantity: 1
    };
  }
  return item;
}
